import { useState, useEffect, useCallback } from 'react';
import { Table, Badge, Button, Modal, Input, Select, Pagination } from '../../components/ui';
import ImagePickerField from '../../components/ui/ImagePickerField';
import { apiFetch } from '../../services/api';

const STATUS_COLORS = {
  open:        'var(--green-light)',
  closed:      'var(--text-muted)',
  coming_soon: 'var(--yellow)',
};

const APP_STATUS_COLORS = {
  applied:   'var(--blue-light)',
  reviewing: 'var(--yellow)',
  accepted:  'var(--green-light)',
  active:    'var(--accent)',
  completed: 'var(--green-light)',
  rejected:  'var(--red-light)',
  withdrawn: 'var(--text-muted)',
};

export default function InternshipsPage() {
  const [tab, setTab]                   = useState('listings');
  const [internships, setInternships]   = useState([]);
  const [applications, setApplications] = useState([]);
  const [pagination, setPag]            = useState(null);
  const [loading, setLoading]           = useState(true);
  const [page, setPage]                 = useState(1);
  const [filter, setFilter]             = useState({ status: '' });
  const [modal, setModal]               = useState(null);
  const [selected, setSel]              = useState(null);
  const [saving, setSaving]             = useState(false);
  const [issuing, setIssuing]           = useState(null);
  const [form, setForm]                 = useState({});
  const [appForm, setAppForm]           = useState({});
  const [error, setError]               = useState('');

  const token   = localStorage.getItem('mh_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadInternships = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter.status) params.set('status', filter.status);
      const res  = await apiFetch(`/internships?${params}`, { headers });
      const data = await res.json();
      setInternships(data.data || []); setPag(data.pagination);
    } catch { setInternships([]); } finally { setLoading(false); }
  }, [page, filter]);

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter.status) params.set('status', filter.status);
      const res  = await apiFetch(`/admin/internship-applications?${params}`, { headers });
      const data = await res.json();
      setApplications(data.data || []); setPag(data.pagination);
    } catch { setApplications([]); } finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => {
    if (tab === 'listings') loadInternships();
    else loadApplications();
  }, [tab, loadInternships, loadApplications]);

  useEffect(() => { setPage(1); setFilter({ status: '' }); }, [tab]);

  const emptyForm = () => ({
    title: '', slug: '', description: '', department: '',
    skills_required: '', duration: '', mode: 'in_person',
    stipend_amount: '', is_paid: false, capacity: 1,
    status: 'open', application_deadline: '', start_date: '', end_date: '',
    media_url: '',
  });

  const openCreate = () => { setForm(emptyForm()); setSel(null); setError(''); setModal('create'); };
  const openEdit   = (i) => {
    setForm({
      title:                i.title,
      slug:                 i.slug,
      description:          i.description || '',
      department:           i.department || '',
      skills_required:      (i.skills_required || []).join(', '),
      duration:             i.duration || '',
      mode:                 i.mode || 'in_person',
      stipend_amount:       i.stipend_amount || '',
      is_paid:              i.is_paid || false,
      capacity:             i.capacity || 1,
      status:               i.status,
      application_deadline: i.application_deadline?.slice(0,10) || '',
      start_date:           i.start_date?.slice(0,10) || '',
      end_date:             i.end_date?.slice(0,10) || '',
      media_url:            i.media_url || '',
    });
    setSel(i); setError(''); setModal('edit');
  };

  const openApp = (a) => {
    setAppForm({
      status:           a.status,
      start_date:       a.start_date?.slice(0,10) || '',
      end_date:         a.end_date?.slice(0,10) || '',
      hours_logged:     a.hours_logged || 0,
      supervisor:       a.supervisor || '',
      performance_note: a.performance_note || '',
      review_note:      a.review_note || '',
    });
    setSel(a); setError(''); setModal('app');
  };

  const handleSaveListing = async () => {
    if (!form.title?.trim()) { setError('Title is required'); return; }
    if (!form.slug?.trim())  { setError('Slug is required');  return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        skills_required:      typeof form.skills_required === 'string'
          ? form.skills_required.split(',').map(s => s.trim()).filter(Boolean)
          : form.skills_required,
        stipend_amount:       form.stipend_amount === '' ? 0 : form.stipend_amount,
        application_deadline: form.application_deadline || null,
        start_date:           form.start_date || null,
        end_date:             form.end_date   || null,
      };
      const url    = selected ? `/internships/${selected.id}` : '/internships';
      const method = selected ? 'PUT' : 'POST';
      const res    = await apiFetch(url, { method, headers, body: JSON.stringify(payload) });
      const data   = await res.json();
      if (!res.ok) { setError(data.error || `Save failed (${res.status})`); return; }
      setModal(null); loadInternships();
    } catch { setError('Network error — check backend is running'); } finally { setSaving(false); }
  };

  const handleSaveApp = async () => {
    setSaving(true); setError('');
    try {
      const res  = await apiFetch(`/admin/internship-applications/${selected.id}`, {
        method: 'PUT', headers, body: JSON.stringify(appForm),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Save failed'); return; }
      setModal(null); loadApplications();
    } catch { setError('Network error'); } finally { setSaving(false); }
  };

  const handleIssueCert = async (appId) => {
    if (!window.confirm('Issue internship certificate?')) return;
    setIssuing(appId);
    try {
      const res  = await apiFetch(`/admin/internship-applications/${appId}/certificate`, {
        method: 'POST', headers, body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.data) { alert(`Certificate issued! Hash: ${data.data.cert_hash}`); loadApplications(); }
      else alert(data.error || 'Failed');
    } catch { alert('Failed'); } finally { setIssuing(null); }
  };

  const newAppCount = applications.filter(a => a.status === 'applied').length;

  const INT_COLS = [
    { key: 'media_url', label: '', render: v => (
      <div style={{ width:36, height:36, borderRadius:6, overflow:'hidden', background:'var(--bg-elevated)', display:'flex', alignItems:'center', justifyContent:'center' }}>
        {v ? <img src={v} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : <span style={{ fontSize:14, opacity:0.3, color:'var(--accent)' }}>◈</span>}
      </div>
    )},
    { key: 'title', label: 'Internship', render: (v, row) => (
      <div>
        <div style={{ fontWeight:500 }}>{v}</div>
        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{row.department || '—'} · {row.duration || '—'}</div>
      </div>
    )},
    { key: 'mode', label: 'Mode', render: v => <span style={{ fontSize:12, color:'var(--text-secondary)', textTransform:'capitalize' }}>{v?.replace('_',' ')}</span> },
    { key: 'capacity', label: 'Spots', render: v => <span style={{ fontSize:12, color:'var(--text-muted)' }}>{v}</span> },
    { key: 'is_paid', label: 'Stipend', render: (v, row) => v
      ? <span style={{ fontSize:12, color:'var(--green-light)' }}>RWF {Number(row.stipend_amount||0).toLocaleString()}</span>
      : <span style={{ fontSize:12, color:'var(--text-muted)' }}>Unpaid</span>
    },
    { key: 'status', label: 'Status', render: v => (
      <span style={{ fontSize:11, fontWeight:600, color:STATUS_COLORS[v]||'var(--text-muted)', textTransform:'capitalize' }}>{v?.replace('_',' ')}</span>
    )},
    { key: 'application_deadline', label: 'Deadline', render: v => v
      ? <span style={{ fontSize:11, color:'var(--text-muted)' }}>{new Date(v).toLocaleDateString('en-RW', { day:'numeric', month:'short', year:'numeric' })}</span>
      : <span style={{ fontSize:11, color:'var(--text-muted)' }}>—</span>
    },
    { key: '_actions', label: '', render: (_, row) => (
      <div style={{ display:'flex', gap:6 }} onClick={e => e.stopPropagation()}>
        <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>Edit</Button>
      </div>
    )},
  ];

  const APP_COLS = [
    { key: 'full_name', label: 'Applicant', render: (v, row) => (
      <div>
        <div style={{ fontWeight:500 }}>{v}</div>
        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{row.email}</div>
        {row.phone && <div style={{ fontSize:10, color:'var(--text-muted)' }}>{row.phone}</div>}
      </div>
    )},
    { key: 'internship_title', label: 'Internship', render: (v, row) => (
      <div>
        <div style={{ fontSize:13 }}>{v || '—'}</div>
        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{row.department || ''}</div>
      </div>
    )},
    { key: 'status', label: 'Status', render: v => (
      <span style={{ fontSize:11, fontWeight:600, color:APP_STATUS_COLORS[v]||'var(--text-muted)', textTransform:'capitalize' }}>{v}</span>
    )},
    { key: 'hours_logged', label: 'Hours', render: v => <span style={{ fontSize:12, color:'var(--text-muted)' }}>{v||0}h</span> },
    { key: 'certificate_id', label: 'Cert', render: v => v
      ? <span style={{ fontSize:10, color:'var(--green-light)' }}>✓ Issued</span>
      : <span style={{ fontSize:10, color:'var(--text-muted)' }}>—</span>
    },
    { key: 'created_at', label: 'Applied', render: v => (
      <div>
        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{new Date(v).toLocaleDateString('en-RW', { day:'numeric', month:'short', year:'numeric' })}</div>
        <div style={{ fontSize:10, color:'var(--text-muted)', opacity:0.7 }}>{new Date(v).toLocaleTimeString('en-RW', { hour:'2-digit', minute:'2-digit' })}</div>
      </div>
    )},
    { key: '_actions', label: '', render: (_, row) => (
      <div style={{ display:'flex', gap:6 }} onClick={e => e.stopPropagation()}>
        <Button variant="ghost" size="sm" onClick={() => openApp(row)}>Manage</Button>
        {row.status === 'completed' && !row.certificate_id && (
          <Button variant="secondary" size="sm" loading={issuing===row.id} onClick={() => handleIssueCert(row.id)}>Cert</Button>
        )}
      </div>
    )},
  ];

  const tabBtn = (t) => ({
    padding:'9px 18px', fontSize:13, cursor:'pointer', background:'none', border:'none',
    borderBottom:`2px solid ${tab===t?'var(--accent)':'transparent'}`,
    color: tab===t ? 'var(--accent)' : 'var(--text-muted)',
    fontWeight: tab===t ? 600 : 400, marginBottom:-1, transition:'all 0.15s',
  });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>

      <div style={{ display:'flex', gap:4, borderBottom:'1px solid var(--border)' }}>
        <button style={tabBtn('listings')}     onClick={() => setTab('listings')}>Internship Listings</button>
        <button style={tabBtn('applications')} onClick={() => setTab('applications')}>
          Applications
          {newAppCount > 0 && (
            <span style={{ marginLeft:6, fontSize:10, padding:'1px 6px', borderRadius:20, background:'rgba(232,164,58,0.2)', color:'var(--accent)', border:'1px solid rgba(232,164,58,0.3)' }}>
              {newAppCount} new
            </span>
          )}
        </button>
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', gap:10 }}>
          <Select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))} style={{ width:160 }}>
            {tab === 'listings' ? <>
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="coming_soon">Coming Soon</option>
            </> : <>
              <option value="">All Statuses</option>
              <option value="applied">Applied</option>
              <option value="reviewing">Reviewing</option>
              <option value="accepted">Accepted</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </>}
          </Select>
          <span style={{ fontSize:12, color:'var(--text-muted)', alignSelf:'center' }}>{pagination?.total ?? 0} records</span>
        </div>
        {tab === 'listings' && <Button onClick={openCreate}>+ New Internship</Button>}
      </div>

      <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
        {tab === 'listings'
          ? <Table columns={INT_COLS} data={internships}  loading={loading} onRowClick={openEdit}/>
          : <Table columns={APP_COLS} data={applications} loading={loading} onRowClick={openApp}/>
        }
        <Pagination pagination={pagination} onPage={setPage}/>
      </div>

      {/* Create / Edit modal */}
      <Modal open={modal === 'create' || modal === 'edit'} onClose={() => setModal(null)}
        title={modal === 'create' ? 'New Internship' : `Edit — ${selected?.title}`} width={680}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          {error && (
            <div style={{ gridColumn:'1 / -1', padding:'10px 14px', background:'rgba(240,96,96,0.1)', border:'1px solid rgba(240,96,96,0.3)', borderRadius:8, fontSize:13, color:'#f06060' }}>
              ⚠ {error}
            </div>
          )}
          <div style={{ gridColumn:'1 / -1' }}>
            <ImagePickerField label="Cover Image" value={form.media_url} onChange={url => setForm(f => ({ ...f, media_url: url }))}/>
          </div>
          <div style={{ gridColumn:'1 / -1' }}>
            <Input label="Title" value={form.title} onChange={e => {
              const slug = e.target.value.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');
              setForm(f => ({ ...f, title: e.target.value, slug }));
            }}/>
          </div>
          <Input label="Slug" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} style={{ fontFamily:'monospace' }}/>
          <Input label="Department" value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} placeholder="e.g. Technology"/>
          <Input label="Duration" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} placeholder="e.g. 3 months"/>
          <Select label="Mode" value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value }))}>
            <option value="in_person">In Person</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
          </Select>
          <Input label="Capacity (spots)" type="number" value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: parseInt(e.target.value)||1 }))}/>
          <Select label="Status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="coming_soon">Coming Soon</option>
          </Select>
          <Input label="Application Deadline" type="date" value={form.application_deadline} onChange={e => setForm(f => ({ ...f, application_deadline: e.target.value }))}/>
          <Input label="Start Date" type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}/>
          <Input label="End Date"   type="date" value={form.end_date}   onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}/>
          <div style={{ gridColumn:'1 / -1', display:'flex', gap:20, alignItems:'center' }}>
            <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
              <input type="checkbox" checked={form.is_paid} onChange={e => setForm(f => ({ ...f, is_paid: e.target.checked }))}/>
              <span style={{ color:'var(--text-secondary)' }}>Paid Internship</span>
            </label>
            {form.is_paid && (
              <Input label="Stipend (RWF)" type="number" value={form.stipend_amount}
                onChange={e => setForm(f => ({ ...f, stipend_amount: e.target.value }))} style={{ width:160 }}/>
            )}
          </div>
          <div style={{ gridColumn:'1 / -1' }}>
            <Input label="Skills Required (comma-separated)" value={form.skills_required}
              onChange={e => setForm(f => ({ ...f, skills_required: e.target.value }))}
              placeholder="React, Node.js, Design"/>
          </div>
          <div style={{ gridColumn:'1 / -1' }}>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={4}
              style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'9px 12px', fontSize:13, resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }}/>
          </div>
          <div style={{ gridColumn:'1 / -1', display:'flex', gap:10, justifyContent:'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSaveListing} loading={saving}>{modal === 'create' ? 'Create Internship' : 'Save Changes'}</Button>
          </div>
        </div>
      </Modal>

      {/* Manage application modal */}
      <Modal open={modal === 'app'} onClose={() => setModal(null)}
        title={`Application — ${selected?.full_name}`} width={560}>
        {selected && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', padding:'12px 16px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {[
                ['Internship', selected.internship_title || '—'],
                ['Email',      selected.email],
                ['Phone',      selected.phone || '—'],
                ['Department', selected.department || '—'],
                ['Date Applied', new Date(selected.created_at).toLocaleDateString('en-RW', { day:'numeric', month:'long', year:'numeric' })],
                ['Time Applied', new Date(selected.created_at).toLocaleTimeString('en-RW', { hour:'2-digit', minute:'2-digit', second:'2-digit' })],
              ].map(([k,v]) => (
                <div key={k}>
                  <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>{k}</div>
                  <div style={{ fontSize:12 }}>{v}</div>
                </div>
              ))}
            </div>

            {selected.portfolio_url && (
              <a href={selected.portfolio_url} target="_blank" rel="noopener noreferrer"
                style={{ fontSize:12, color:'var(--accent)', display:'inline-flex', alignItems:'center', gap:4 }}>
                🔗 View Portfolio / CV
              </a>
            )}

            {selected.cover_letter && (
              <div style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', padding:'10px 14px', borderLeft:'2px solid var(--accent)' }}>
                <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Cover Letter</div>
                <p style={{ fontSize:12, color:'var(--text-secondary)', lineHeight:1.6 }}>{selected.cover_letter}</p>
              </div>
            )}

            {error && (
              <div style={{ padding:'8px 12px', background:'rgba(240,96,96,0.1)', border:'1px solid rgba(240,96,96,0.3)', borderRadius:6, fontSize:12, color:'#f06060' }}>⚠ {error}</div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <Select label="Status" value={appForm.status} onChange={e => setAppForm(f => ({ ...f, status: e.target.value }))}>
                <option value="applied">Applied</option>
                <option value="reviewing">Reviewing</option>
                <option value="accepted">Accepted</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
                <option value="withdrawn">Withdrawn</option>
              </Select>
              <Input label="Supervisor" value={appForm.supervisor} onChange={e => setAppForm(f => ({ ...f, supervisor: e.target.value }))}/>
              <Input label="Start Date" type="date" value={appForm.start_date} onChange={e => setAppForm(f => ({ ...f, start_date: e.target.value }))}/>
              <Input label="End Date"   type="date" value={appForm.end_date}   onChange={e => setAppForm(f => ({ ...f, end_date: e.target.value }))}/>
              <div style={{ gridColumn:'1 / -1' }}>
                <label style={labelStyle}>Hours Logged</label>
                <input type="number" step="0.5" value={appForm.hours_logged}
                  onChange={e => setAppForm(f => ({ ...f, hours_logged: e.target.value }))}
                  style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'8px 12px', fontSize:13, boxSizing:'border-box' }}/>
              </div>
              <div style={{ gridColumn:'1 / -1' }}>
                <label style={labelStyle}>Performance Note</label>
                <textarea value={appForm.performance_note} onChange={e => setAppForm(f => ({ ...f, performance_note: e.target.value }))} rows={3}
                  style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'9px 12px', fontSize:13, resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }}/>
              </div>
              <div style={{ gridColumn:'1 / -1' }}>
                <label style={labelStyle}>Internal Review Note</label>
                <textarea value={appForm.review_note} onChange={e => setAppForm(f => ({ ...f, review_note: e.target.value }))} rows={2}
                  style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'9px 12px', fontSize:13, resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }}/>
              </div>
            </div>

            <div style={{ display:'flex', gap:10, justifyContent:'space-between' }}>
              <div>
                {appForm.status === 'completed' && !selected.certificate_id && (
                  <Button variant="secondary" loading={issuing===selected.id}
                    onClick={() => { setModal(null); handleIssueCert(selected.id); }}>
                    Issue Certificate
                  </Button>
                )}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
                <Button onClick={handleSaveApp} loading={saving}>Save Changes</Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

const labelStyle = { fontSize:12, color:'var(--text-secondary)', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.03em', display:'block', marginBottom:6 };
